import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, IconButton, Chip } from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import { useTheme } from '../../utils/ThemeContext';
import { useInteractiveTutorial } from '../../utils/InteractiveTutorialContext';
import '../../assets/css/futuristic-modern.css';

/**
 * Interactive Tutorial Overlay
 * Blurs entire page EXCEPT the focused element, with floating instructions nearby
 */
const InteractiveTutorialOverlay = () => {
  const { isDarkMode } = useTheme();
  const {
    isActive,
    getCurrentStep,
    currentStepIndex,
    getTotalSteps,
    getProgress,
    nextStep,
    previousStep,
    skipTutorial,
    finishTutorial
  } = useInteractiveTutorial();

  const [focusRect, setFocusRect] = useState(null);
  const [instructionPosition, setInstructionPosition] = useState({ top: 0, left: 0 });
  const animationFrameRef = useRef(null);
  const instructionRef = useRef(null);

  const step = getCurrentStep();
  const totalSteps = getTotalSteps();
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  // Dispatch step change event for tab switching
  useEffect(() => {
    if (isActive && step) {
      window.dispatchEvent(new CustomEvent('tutorialStepChange', {
        detail: { stepId: step.id }
      }));
    }
  }, [isActive, step, currentStepIndex]);

  // Update focus area position and instruction position
  useEffect(() => {
    if (!isActive || !step || !step.targetElement) {
      setFocusRect(null);
      return;
    }

    const isVisible = (el) => {
      if (!el || el.offsetParent === null) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom >= 0 && r.right >= 0 && r.top <= (window.innerHeight || document.documentElement.clientHeight) && r.left <= (window.innerWidth || document.documentElement.clientWidth);
    };

    const updatePositions = () => {
      let element = null;
      if (step.targetElement) {
        const all = document.querySelectorAll(step.targetElement);
        // Prefer first visible element in viewport
        element = Array.from(all).find(isVisible) || all[0] || null;
      }
      
      if (element) {
        // Ensure element is scrolled into view if offscreen
        if (!isVisible(element)) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
        let rect = element.getBoundingClientRect();
        
        
        // Check for dynamic popups (DatePicker calendar, Select dropdown, Menus, etc.)
        // Date picker specific selectors first (prioritized)
        const datePickerSelectors = [
          '.react-datepicker-popper',
          '.react-datepicker__portal'
        ];
        
        const otherPopupSelectors = [
          // MUI Popover/Menu (general)
          '.MuiPopover-root',
          '.MuiMenu-root',
          // Paper containers for menus/popovers
          '.MuiPaper-root.MuiMenu-paper',
          '.MuiPaper-root.MuiPopover-paper',
          // MUI X pickers (if used)
          '.MuiPickersPopper-root',
          // Generic role-based fallbacks
          '[role="menu"]',
          '[role="listbox"]'
        ];

        // Find the nearest visible popup to the element (by center distance)
        const popupCandidates = [];
        
        // Check date pickers first - prioritize the outermost container
        datePickerSelectors.forEach(selector => {
          document.querySelectorAll(selector).forEach(node => {
            // For react-datepicker-popper, check if it contains a .react-datepicker child
            // and use the child's dimensions (which has the actual visual styling)
            let targetNode = node;
            if (node.classList.contains('react-datepicker-popper')) {
              const innerCalendar = node.querySelector('.react-datepicker');
              if (innerCalendar) {
                targetNode = innerCalendar;
              }
            }
            
            const r = targetNode.getBoundingClientRect();
            if (r && r.width > 0 && r.height > 0) {
              popupCandidates.push({ node: targetNode, rect: r, isDatePicker: true, priority: 1 });
            }
          });
        });
        
        // Then check other popups
        otherPopupSelectors.forEach(selector => {
          document.querySelectorAll(selector).forEach(node => {
            const r = node.getBoundingClientRect();
            if (r && r.width > 0 && r.height > 0) {
              popupCandidates.push({ node, rect: r, isDatePicker: false, priority: 2 });
            }
          });
        });

        if (popupCandidates.length > 0) {
          const elCenter = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
          let bestPopup = null;
          let bestRect = null;
          let bestDist = Infinity;
          let bestPriority = Infinity;
          let isDatePicker = false;
          
          popupCandidates.forEach(c => {
            const pr = c.rect;
            const pc = { x: pr.left + pr.width / 2, y: pr.top + pr.height / 2 };
            const dx = pc.x - elCenter.x;
            const dy = pc.y - elCenter.y;
            const dist = dx * dx + dy * dy;
            
            // Prioritize by priority first, then by distance
            // Priority 1 (date pickers) always win over Priority 2 (other popups)
            const shouldReplace = c.priority < bestPriority || 
                                  (c.priority === bestPriority && dist < bestDist);
            
            if (shouldReplace) {
              bestDist = dist;
              bestPriority = c.priority;
              bestPopup = c.node;
              bestRect = pr;
              isDatePicker = c.isDatePicker;
            }
          });
          
          if (bestRect && bestPopup) {
            // Add generous padding for date pickers to ensure full calendar is visible
            // Regular popups get standard padding
            const padding = isDatePicker ? 24 : 8;
            
            rect = {
              top: bestRect.top - padding,
              left: bestRect.left - padding,
              width: bestRect.width + (padding * 2),
              height: bestRect.height + (padding * 2)
            };
          }
        }
        
        const newFocusRect = {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        };
        
        setFocusRect(newFocusRect);

        // Calculate instruction position (around the element, not overlapping)
        const gap = 30; // Gap between element and instructions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        // Measure actual instruction size if available to avoid overflow
        const measuredWidth = instructionRef.current?.offsetWidth || 320;
        const measuredHeight = instructionRef.current?.offsetHeight || 250;

        // Calculate element bounds
        const elementRight = rect.left + rect.width;
        const elementBottom = rect.top + rect.height;

        let top = rect.top;
        let left = elementRight + gap;
        let placement = 'right';

        // Try RIGHT first
        if (left + measuredWidth > viewportWidth - 20) {
          // Try LEFT
          left = rect.left - measuredWidth - gap;
          placement = 'left';
          
          if (left < 20) {
            // Try BELOW
            left = rect.left;
            top = elementBottom + gap;
            placement = 'below';
            
            if (top + measuredHeight > viewportHeight - 20) {
              // Try ABOVE
              top = rect.top - measuredHeight - gap;
              placement = 'above';
            }
          }
        }

        // Center vertically when on left/right
        if (placement === 'right' || placement === 'left') {
          top = rect.top + (rect.height / 2) - (measuredHeight / 2);
        }

        // Keep within viewport bounds with margins
        top = Math.max(20, Math.min(top, viewportHeight - measuredHeight - 20));
        left = Math.max(20, Math.min(left, viewportWidth - measuredWidth - 20));

        
        setInstructionPosition({ top, left });
      } else {
        setFocusRect(null);
      }
    };

    // Initial update
    updatePositions();

    // Update on scroll and resize
    const handleUpdate = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(updatePositions);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Observe DOM changes
    const observer = new MutationObserver(handleUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      observer.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, step]);

  if (!isActive || !step) {
    return null;
  }

  

  // Blur overlay color based on theme
  const blurOverlayColor = isDarkMode 
    ? 'rgba(0, 0, 0, 0.75)'
    : 'rgba(255, 255, 255, 0.85)';

  const padding = 16; // Padding around focus element

  return (
    <>
      {/* Blur Overlay with Clear Focus Area */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          pointerEvents: 'none'
        }}
      >
        {focusRect ? (
          // Create a blur effect everywhere EXCEPT the focused element
          <>
            {/* Top section */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: Math.max(0, focusRect.top - padding),
                backgroundColor: blurOverlayColor,
                backdropFilter: 'blur(6px)',
                transition: 'all 0.3s ease'
              }}
            />

            {/* Left section (middle row) */}
            <Box
              sx={{
                position: 'absolute',
                top: Math.max(0, focusRect.top - padding),
                left: 0,
                width: Math.max(0, focusRect.left - padding),
                height: focusRect.height + (padding * 2),
                backgroundColor: blurOverlayColor,
                backdropFilter: 'blur(6px)',
                transition: 'all 0.3s ease'
              }}
            />

            {/* Right section (middle row) */}
            <Box
              sx={{
                position: 'absolute',
                top: Math.max(0, focusRect.top - padding),
                left: focusRect.left + focusRect.width + padding,
                right: 0,
                height: focusRect.height + (padding * 2),
                backgroundColor: blurOverlayColor,
                backdropFilter: 'blur(6px)',
                transition: 'all 0.3s ease'
              }}
            />

            {/* Bottom section */}
            <Box
              sx={{
                position: 'absolute',
                top: focusRect.top + focusRect.height + padding,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: blurOverlayColor,
                backdropFilter: 'blur(6px)',
                transition: 'all 0.3s ease'
              }}
            />

            {/* Glowing border around focused element */}
            <Box
              sx={{
                position: 'absolute',
                top: focusRect.top - padding,
                left: focusRect.left - padding,
                width: focusRect.width + (padding * 2),
                height: focusRect.height + (padding * 2),
                border: '3px solid rgba(108, 48, 130, 0.8)',
                borderRadius: '12px',
                boxShadow: '0 0 0 4px rgba(108, 48, 130, 0.18), 0 0 20px rgba(108, 48, 130, 0.35)',
                pointerEvents: 'none',
                transition: 'all 0.3s ease',
                animation: 'tutorial-pulse 2s ease-in-out infinite'
              }}
            />
          </>
        ) : (
          // Full blur overlay when no specific element (info steps)
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: blurOverlayColor,
              backdropFilter: 'blur(6px)',
              transition: 'all 0.3s ease'
            }}
          />
        )}
      </Box>

      {/* Floating Instructions - NO CARD, just floating text */}
      <Box
        ref={instructionRef}
        sx={{
          position: 'fixed',
          top: focusRect ? `${instructionPosition.top}px` : '50%',
          left: focusRect ? `${instructionPosition.left}px` : '50%',
          transform: focusRect ? 'none' : 'translate(-50%, -50%)',
          zIndex: 10000,
          maxWidth: '320px',
          pointerEvents: 'auto',
          animation: 'tutorial-float-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          transition: 'all 0.3s ease',
          // NO BACKGROUND - text floats freely!
          background: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          padding: 0,
          textShadow: isDarkMode 
            ? '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 2px rgba(0, 0, 0, 0.5)'
            : '0 2px 8px rgba(255, 255, 255, 0.9), 0 0 2px rgba(255, 255, 255, 0.7)'
        }}
      >
        {/* Close button - floating at top right */}
        {step.allowSkip !== false && (
          <IconButton
            onClick={skipTutorial}
            size="small"
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              width: 32,
              height: 32,
              backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              color: isDarkMode ? '#ffffff' : '#000000',
              border: '2px solid #6c3082',
              boxShadow: '0 4px 12px rgba(108, 48, 130, 0.4)',
              '&:hover': {
                backgroundColor: '#6c3082',
                color: '#ffffff',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        )}

        {/* Step indicator - floating badge */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.95)',
            color: '#6c3082',
            padding: '6px 12px',
            borderRadius: '20px',
            border: '2px solid #6c3082',
            fontSize: '0.75rem',
            fontWeight: 700,
            mb: 2,
            boxShadow: '0 4px 12px rgba(108, 48, 130, 0.2)',
            letterSpacing: '0.5px'
          }}
        >
          <LightbulbIcon style={{ fontSize: 16, color: '#6c3082' }} />
          STEP {currentStepIndex + 1}/{totalSteps}
        </Box>

        {/* Title - large, bold, floating */}
        <Typography
          sx={{
            color: isDarkMode ? '#ffffff' : '#000000',
            fontWeight: 800,
            fontSize: '1.4rem',
            lineHeight: 1.2,
            mb: 1.5,
            letterSpacing: '-0.5px'
          }}
        >
          {step.title}
        </Typography>

        {/* Message - clear and readable */}
        <Typography
          sx={{
            color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.85)',
            fontSize: '1rem',
            lineHeight: 1.6,
            mb: 2,
            fontWeight: 400,
            '& strong': {
              fontWeight: 700,
              color: isDarkMode ? '#ffffff' : '#000000'
            }
          }}
          dangerouslySetInnerHTML={{ __html: step.message }}
        />

        {/* Instruction - prominent with arrow */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            backgroundColor: 'rgba(255, 193, 7, 0.6)',
            color: '#000000',
            padding: '12px 16px',
            borderRadius: '0px',
            mb: step.helpText ? 1.5 : 2.5,
            border: '2px solid #ffc107',
            boxShadow: '0 4px 16px rgba(255, 193, 7, 0.35)'
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '1rem',
              lineHeight: 1.4
            }}
          >
            {step.instruction}
          </Typography>
        </Box>

        {/* Help text - subtle hint */}
        {step.helpText && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 2.5
            }}
          >
            <LightbulbIcon style={{ 
              fontSize: 20, 
              color: '#FFD700',
              flexShrink: 0,
              filter: 'drop-shadow(0 2px 4px rgba(255, 215, 0, 0.5))'
            }} />
            <Typography
              sx={{
                color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                fontStyle: 'italic'
              }}
            >
              {step.helpText}
            </Typography>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Back button (for all non-first steps) */}
          {!isFirstStep && (
            <Button
              onClick={previousStep}
              startIcon={<ArrowBackIcon style={{ fontSize: 18 }} />}
              size="small"
              sx={{
                color: 'var(--text-secondary)',
                fontSize: '0.8rem',
                textTransform: 'none',
                minWidth: 'auto',
                padding: '4px 12px',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                  color: 'var(--neon-cyan)'
                }
              }}
            >
              Back
            </Button>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Next/Continue button (for all non-last steps) */}
          {!isLastStep && (
            <Button
              onClick={nextStep}
              endIcon={<ArrowForwardIcon style={{ fontSize: 18 }} />}
              size="small"
              aria-label={isFirstStep ? 'Start tutorial' : 'Continue tutorial'}
              sx={{
                background: 'var(--accent-gradient)',
                color: '#ffffff !important',
                fontWeight: 700,
                fontSize: '0.9rem',
                textTransform: 'none',
                padding: '8px 18px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(6px) saturate(1.1)',
                boxShadow: '0 4px 14px rgba(108, 48, 130, 0.28)',
                transition: 'all 0.2s ease',
                '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                  color: '#ffffff'
                },
                '&:hover': {
                  background: 'var(--accent-gradient)',
                  boxShadow: '0 8px 18px rgba(108, 48, 130, 0.38)',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0) scale(0.99)'
                },
                '&:focus-visible': {
                  outline: 'none',
                  boxShadow: '0 0 0 3px rgba(255,255,255,0.4), 0 0 0 6px rgba(108,48,130,0.4)'
                },
              }}
            >
              {isFirstStep ? 'Start' : 'Continue'}
            </Button>
          )}

          {/* Finish button (last step) */}
          {step.type === 'info' && isLastStep && (
            <Button
              onClick={finishTutorial}
              endIcon={<CheckCircleIcon style={{ fontSize: 18 }} />}
              size="small"
              sx={{
                background: 'rgba(255, 193, 7, 0.9)',
                color: '#000000',
                fontWeight: 700,
                fontSize: '0.9rem',
                textTransform: 'none',
                padding: '8px 18px',
                borderRadius: '10px',
                border: '2px solid #ffc107',
                boxShadow: '0 4px 16px rgba(255, 193, 7, 0.35)',
                '&:hover': {
                  background: 'rgba(255, 193, 7, 1)',
                  boxShadow: '0 6px 20px rgba(255, 193, 7, 0.45)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Finish
            </Button>
          )}
        </Box>
      </Box>

      {/* Animations */}
      <style>
        {`
          @keyframes tutorial-pulse {
            0%, 100% {
              box-shadow: 0 0 0 4px rgba(108, 48, 130, 0.18), 0 0 20px rgba(108, 48, 130, 0.35);
            }
            50% {
              box-shadow: 0 0 0 6px rgba(108, 48, 130, 0.14), 0 0 30px rgba(108, 48, 130, 0.5);
            }
          }

          @keyframes tutorial-float-in {
            from {
              opacity: 0;
              transform: ${focusRect ? 'translateY(-10px)' : 'translate(-50%, -60%)'};
            }
            to {
              opacity: 1;
              transform: ${focusRect ? 'translateY(0)' : 'translate(-50%, -50%)'};
            }
          }

          @keyframes tutorial-dots {
            0%, 20% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              opacity: 1;
            }
          }
        `}
      </style>
    </>
  );
};

export default InteractiveTutorialOverlay;
